import { Injectable } from '@angular/core';

import { Observable, of, forkJoin } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';

import { utils } from '@bayclubs/utils';

import { SubstitutionBoard, SubstitutionBoardEnumerations, Providers } from '../../interfaces/interfaces.module';
import { GroupClassesApiService, SubBoardApiService, ProvidersApiService } from '../../shared/shared.module';

import { SubBoardListItemModel } from '../models/sub-board-list-item-model';
import { ProposalListItemModel } from '../models/proposal-list-item-model';
import { ConflictModel, GroupClassDetailsModel } from '@bayclubs/group-classes-employee-api';

@Injectable({
    providedIn: 'root'
})
export class SubBoardListGatewayApiService {
    constructor(
        private api: SubBoardApiService,
        private groupClassApi: GroupClassesApiService,
        private providersApi: ProvidersApiService
    ) {}

    public getSubBoardListItem(
        clubId: string,
        requestId: string,
        showLoader: boolean = true
    ): Observable<SubBoardListItemModel> {
        const details$: Observable<SubstitutionBoard.SubstitutionRequestListItemModel> = this.api.getDetails(
            clubId,
            requestId,
            showLoader
        );
        return details$.pipe(
            map(request => [request]),
            flatMap(requests => this.getGroupClassesAndJoinWithSubstitutionRequests(requests, false)),
            map(items => items[0])
        );
    }

    public conflict(groupClassId: string, instructorId: string): Observable<ConflictModel> {
        return this.groupClassApi.conflict(groupClassId, instructorId);
    }

    public getSubBoardListItems(
        clubId: string,
        instructorId: string,
        categoryCodes: string[]
    ): Observable<SubBoardListItemModel[]> {
        const query: SubstitutionBoard.GetSubstitutionRequestsQuery = {
            providerId: instructorId,
            categoryCodes: categoryCodes
        } as SubstitutionBoard.GetSubstitutionRequestsQuery;

        const requestsObservable$ = this.api.list(clubId, query).pipe(
            map(x => x.items),
            flatMap((requests: SubstitutionBoard.SubstitutionRequestListItemModel[]) =>
                this.getGroupClassesAndJoinWithSubstitutionRequests(requests)
            )
        );

        return requestsObservable$;
    }

    public getSubBoardListItemsAdvanced(
        clubId: string,
        instructorId: string,
        categoryCodes: string[],
        dateFrom: utils.BcDate,
        dateTo: utils.BcDate,
        status: string
    ): Observable<SubBoardListItemModel[]> {
        const query: SubstitutionBoard.GetSubstitutionRequestsQuery = {
            advMode: true,
            providerId: instructorId,
            categoryCodes: categoryCodes,
            dateFrom: dateFrom,
            dateTo: dateTo,
            status: status
        };

        const requestsObservable$ = this.api.list(clubId, query).pipe(
            map(x => x.items),
            flatMap((requests: SubstitutionBoard.SubstitutionRequestListItemModel[]) =>
                this.getGroupClassesAndJoinWithSubstitutionRequests(requests)
            )
        );

        return requestsObservable$;
    }

    private getGroupClassesAndJoinWithSubstitutionRequests(
        requests: SubstitutionBoard.SubstitutionRequestListItemModel[],
        showLoader: boolean = true
    ): Observable<SubBoardListItemModel[]> {
        const providerIds = new Set<string>();
        for (const request of requests) {
            if (request.providerId) {
                providerIds.add(request.providerId);
            }

            if (request.approvedProviderId) {
                providerIds.add(request.approvedProviderId);
            }

            for (const proposal of request.proposals) {
                providerIds.add(proposal.providerId);
            }
        }

        return forkJoin({
            requests: of(requests),
            groupClasses: this.groupClassApi.getManyDetails(requests.map(r => r.activityId), showLoader),
            providers: this.providersApi.getMany(Array.from(providerIds), showLoader)
        }).pipe(map(result => this.generateModels(result.requests, result.groupClasses.items, result.providers)));
    }

    private generateModels(
        requests: SubstitutionBoard.SubstitutionRequestListItemModel[],
        groupClasses: GroupClassDetailsModel[],
        providers: Providers.BasicProviderModel[]
    ): SubBoardListItemModel[] {
        return requests
            .map(x => {
                const groupClass = groupClasses.find(gc => gc.id === x.activityId);
                if (!groupClass) {
                    // If respective groupClass is not found, be robust and show others
                    return null;
                }
                const instructor = providers.find(pr => pr.id === x.providerId);
                const photoUrl = instructor && instructor.photoUrl ? this.getRawPhotoUrl(instructor.photoUrl) : '';
                const proposals: ProposalListItemModel[] = x.proposals.map((prop: SubstitutionBoard.ProposalListItemModel) => {
                    const providerModel: Providers.BasicProviderModel = providers.find(pr => pr.id === prop.providerId);
                    return {
                        id: prop.id,
                        providerId: prop.providerId,
                        providerFirstName: providerModel ? providerModel.firstName : 'Not',
                        providerLastName: providerModel ? providerModel.lastName : 'Available',
                        proposalCreationDate: prop.proposalCreationDate,
                        providerComment: prop.providerComment,
                        providerPhotoUrl: providerModel ? providerModel.photoUrl : null,
                        status: prop.status
                    };
                });

                const substituteInstructor = providers.find(pr => pr.id === x.approvedProviderId);
                return SubBoardListItemModel.from({
                    id: x.id,
                    clubId: x.clubId,
                    classId: x.activityId,
                    className: groupClass.name,
                    classCategory: groupClass.category.name,
                    classLocation: groupClass.location ? groupClass.location.name : '',
                    classDate: groupClass.date,
                    classTimeInMinutesFrom: groupClass.timeFromInMinutes,
                    classTimeInMinutesTo: groupClass.timeToInMinutes,
                    instructorFirstName: instructor ? instructor.firstName : 'Not',
                    instructorLastName: instructor ? instructor.lastName : 'Assigned',
                    instructorPhotoUrl: photoUrl,
                    offersCount: x.proposals
                        ? x.proposals.filter(
                              p => p.status.code === SubstitutionBoardEnumerations.SubstitutionProposalStatus.pending.code
                          ).length
                        : 0,
                    clubName: groupClass.club.name,
                    clubShortName: groupClass.club.shortName,
                    clubTimezone: groupClass.club.timezone,
                    status: x.status,
                    submitterComment: x.submitterComment,
                    substituteFirstName:
                        x.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.closed.code &&
                        substituteInstructor
                            ? substituteInstructor.firstName
                            : null,
                    substituteLastName:
                        x.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.closed.code &&
                        substituteInstructor
                            ? substituteInstructor.lastName
                            : null,
                    substitutePhotoUrl:
                        x.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.closed.code &&
                        substituteInstructor
                            ? this.getRawPhotoUrl(substituteInstructor.photoUrl)
                            : null,
                    proposals: proposals
                });
            })
            .filter(x => x); // skip nulls
    }

    private getRawPhotoUrl(url: string) {
        if (!url) {
            return url;
        }
        return url + '?format=raw&&height=80';
    }
}
